import { FormEvent, ReactElement, ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Details, Fieldset, Heading, Paragraph, Tag, ValidationMessage } from '@digdir/designsystemet-react';
import type { MeasureArea, ProposalError, ProposalRequest, TeamMeasures } from '../../../api/workEnvironmentTypes';
import { useProposeMeasureMutation } from '../../../hooks/mutations/useTeamMeasuresMutations';
import { FieldError, validationErrors } from '../../WorkEnvironmentSurveyDetail/assessmentView';
import { PROPOSAL_FIELDS, proposalFieldErrors, proposeErrorKey } from '../../WorkEnvironmentSurveyDetail/measuresView';
import { areaName, LEVEL_COLORS } from '../../WorkEnvironmentSurveyDetail/riskView';
import { othersOpen, proposalAreaSections, sentCount } from '../teamMeasuresView';
import { ProposalFields } from './ProposalFields';
import form from '../../FormSubmission/WorkEnvironmentFormSubmission.module.css';
import classes from '../WorkEnvironmentTeamMeasures.module.css';

type Props = { uuid: string; measures: TeamMeasures };

const EMPTY = { factorKey: '', title: '', reason: '' };

/** Takes focus when it appears, so screen readers say the proposal is sent */
const SentConfirmation = (): ReactElement => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => ref.current?.focus(), []);

  return (
    <Alert ref={ref} data-color="success" role="status" tabIndex={-1}>
      <Paragraph>{t('workEnvironmentMeasures.team.sentHere')}</Paragraph>
    </Alert>
  );
};

/** Anonymous. A person can send as many proposals as they like before the deadline. */
export const ProposeForm = ({ uuid, measures }: Props): ReactElement => {
  const { t } = useTranslation();
  const [proposal, setProposal] = useState<ProposalRequest>(EMPTY);
  const [errors, setErrors] = useState<FieldError[]>([]);
  const [sentAreaKeys, setSentAreaKeys] = useState<string[]>([]);
  const [confirmedKey, setConfirmedKey] = useState<string | null>(null);
  const [othersExpanded, setOthersExpanded] = useState(false);
  const propose = useProposeMeasureMutation(uuid);
  const areaError = errors.find((error) => error.fieldId === PROPOSAL_FIELDS.area)?.message;
  const { needing, others, foldsOthers, firstKey } = proposalAreaSections(measures.areas);
  const showsOthers = othersOpen(others, {
    expanded: othersExpanded,
    chosenKey: proposal.factorKey,
    hasErrors: errors.length > 0,
  });

  const choose = (factorKey: string) => {
    setProposal({ ...proposal, factorKey });
    setConfirmedKey(null);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const { factorKey } = proposal;
    propose.mutate(proposal, {
      onSuccess: () => {
        setErrors([]);
        setProposal(EMPTY);
        setSentAreaKeys([...sentAreaKeys, factorKey]);
        setConfirmedKey(factorKey);
      },
      onError: (error) => {
        const invalid = validationErrors<ProposalError>(error);
        if (invalid) setErrors(proposalFieldErrors(invalid, t));
      },
    });
  };

  const areaChoice = (area: MeasureArea) => {
    const sent = sentCount(sentAreaKeys, area.key);
    return (
      <li key={area.key} className={classes.area}>
        <label className={classes.choice}>
          <input
            type="radio"
            name="proposal-area"
            id={area.key === firstKey ? PROPOSAL_FIELDS.area : undefined}
            className="ds-input"
            value={area.key}
            checked={proposal.factorKey === area.key}
            onChange={() => choose(area.key)}
          />
          <span className={classes.choiceText}>
            <span className="ds-label">{areaName(area, t)}</span>
            <span className={classes.choiceMeta}>
              {area.level && (
                <Tag data-size="sm" data-color={LEVEL_COLORS[area.level]}>
                  {t(`workEnvironmentRisk.matrix.levels.${area.level}`)}
                </Tag>
              )}
              {sent > 0 && (
                <Tag data-size="sm" data-color="success">
                  {t('workEnvironmentMeasures.team.sentByYou', { count: sent })}
                </Tag>
              )}
              <span className={`ds-paragraph ${form.subtle}`} data-size="xs">
                {area.proposals > 0
                  ? t('workEnvironmentMeasures.areas.proposals', { count: area.proposals })
                  : t('workEnvironmentMeasures.areas.none')}
              </span>
            </span>
          </span>
        </label>
        {proposal.factorKey === area.key && (
          <ProposalFields
            areaName={areaName(area, t)}
            proposal={proposal}
            errors={errors}
            sending={propose.isPending}
            failure={propose.isError && !validationErrors(propose.error) ? proposeErrorKey(propose.error) : null}
            onChange={setProposal}
          />
        )}
        {confirmedKey === area.key && <SentConfirmation />}
      </li>
    );
  };

  const areaList = (areas: MeasureArea[]) => <ul className={classes.choices}>{areas.map(areaChoice)}</ul>;

  const areaGroup = (title: string, list: ReactNode) => (
    <div className={classes.group}>
      <Heading level={3} data-size="2xs">
        {title}
      </Heading>
      {list}
    </div>
  );

  const othersGroup = foldsOthers ? (
    <Details open={showsOthers} onToggle={() => setOthersExpanded(!showsOthers)}>
      <Details.Summary>{t('workEnvironmentMeasures.team.othersCount', { count: others.length })}</Details.Summary>
      <Details.Content>{areaList(others)}</Details.Content>
    </Details>
  ) : (
    others.length > 0 && areaGroup(t('workEnvironmentMeasures.team.others'), areaList(others))
  );

  return (
    <form onSubmit={submit} noValidate className={form.form}>
      <Alert data-color="warning" className={classes.warning}>
        <Heading level={2} data-size="xs">
          {t('workEnvironmentMeasures.team.warningTitle')}
        </Heading>
        <Paragraph data-size="sm">{t('workEnvironmentMeasures.team.warningText')}</Paragraph>
        <Paragraph data-size="sm">{t('workEnvironmentMeasures.team.goodExample')}</Paragraph>
        <Paragraph data-size="sm">{t('workEnvironmentMeasures.team.badExample')}</Paragraph>
      </Alert>
      <Fieldset>
        <Fieldset.Legend>{t('workEnvironmentMeasures.team.areaQuestion')}</Fieldset.Legend>
        <Fieldset.Description>{t('workEnvironmentMeasures.team.areaHelp')}</Fieldset.Description>
        {needing.length > 0 && areaGroup(t('workEnvironmentMeasures.team.needing'), areaList(needing))}
        {othersGroup}
        {areaError && <ValidationMessage>{areaError}</ValidationMessage>}
      </Fieldset>
    </form>
  );
};
